import {
  combinedSettingsSelector,
  combinedStyleSelector,
  makeEntitySelector,
  makeSizeStyleSelector,
  type SettingsPropsParametricSelector,
  TargetSelector
} from "@client/selectors";
import { EntityType } from "@client/types";
import * as React from "react";
import { createSelector } from "reselect";
import { isReverseChildren, isStackChildren, isWrapChildren } from "./lib";

export type LayoutSettings = {
  align: "fill" | "hug" | string;
  direction: "row" | "column";
  gap: string;
  justify: "flex-start" | "center" | "flex-end" | "space-around" | "space-between" | "space-evenly";
  reverse?: boolean;
  wrap?: boolean;
};
const makeLayoutSettingsSelector = (target: string) =>
  combinedSettingsSelector<LayoutSettings>(target, ["direction", "justify", "align", "gap", "reverse", "wrap"]);

export const makeLayoutStyleSelector = (): SettingsPropsParametricSelector => {
  const layoutSettingsSelector = makeLayoutSettingsSelector(TargetSelector.Entity);
  // const positionStyleSelector = makePositionStyleSelector(TargetSelector.Entity);
  const sizeStyleSelector = makeSizeStyleSelector(TargetSelector.Entity);
  const styleSelector = combinedStyleSelector(TargetSelector.Entity, ["margin", "border", "boxShadow", "padding"]);

  return createSelector(
    [sizeStyleSelector, layoutSettingsSelector, styleSelector],
    (sizeStyle, layoutSettings, style): React.CSSProperties => ({
      alignItems: layoutSettings.align,
      justifyContent: layoutSettings.justify,
      gap: layoutSettings.gap,
      flexWrap: isWrapChildren(layoutSettings.wrap) ? "wrap" : "nowrap",
      flexDirection: isStackChildren(layoutSettings.direction)
        ? isReverseChildren(layoutSettings.reverse)
          ? "column-reverse"
          : "column"
        : isReverseChildren(layoutSettings.reverse)
        ? "row-reverse"
        : "row",
      ...sizeStyle,
      ...style
    })
  );
};

export const makeStyleSelector = () => {
  const entitySelector = makeEntitySelector();
  const dimensionSelector = createSelector([entitySelector], (entity) =>
    entity.type === EntityType.Column ? entity.dimensionSize : undefined
  );
  const layoutStyleSelector = makeLayoutStyleSelector();

  return createSelector(
    [dimensionSelector, layoutStyleSelector],
    (dimensionSize, layoutStyle): React.CSSProperties => ({
      ...layoutStyle,
      width: dimensionSize ? `${dimensionSize}%` : layoutStyle.width
    })
  );
};
